import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { SmartNotesTask } from '@/types/contact';
import { toast } from '@/hooks/use-toast';

export function useSmartNotesSettings() {
  const [isLoading, setIsLoading] = useState(true);
  const [enableSmartNotes, setEnableSmartNotes] = useState(false);
  const [autoRunSmartNotes, setAutoRunSmartNotes] = useState(false);
  const [tasks, setTasks] = useState<SmartNotesTask[]>([]);
  const [defaultTaskId, setDefaultTaskId] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.warn('[Smart Notes] No user logged in');
        return;
      }

      // Load settings
      const { data: settings, error: settingsError } = await supabase
        .from('user_settings')
        .select('enable_smart_notes, auto_run_smart_notes')
        .eq('user_id', user.id)
        .maybeSingle();

      if (settingsError) throw settingsError;

      if (settings) {
        setEnableSmartNotes(settings.enable_smart_notes ?? false);
        setAutoRunSmartNotes(settings.auto_run_smart_notes ?? false);
      }

      // Load tasks
      const { data: tasksData, error: tasksError } = await supabase
        .from('smart_notes_tasks')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (tasksError) throw tasksError;

      setTasks(tasksData || []);
      
      const defaultTask = tasksData?.find(t => t.is_default);
      setDefaultTaskId(defaultTask?.id || null);

    } catch (error) {
      console.error('[Smart Notes] Failed to load settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load Smart Notes settings',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSmartNotes = async (enabled: boolean) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: user.id,
          enable_smart_notes: enabled,
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      setEnableSmartNotes(enabled);
      
      toast({
        title: enabled ? 'Smart Notes Enabled' : 'Smart Notes Disabled',
        description: enabled 
          ? 'AI research will be available for your contacts'
          : 'Smart Notes has been turned off',
      });
    } catch (error) {
      console.error('[Smart Notes] Failed to toggle:', error);
      toast({
        title: 'Error',
        description: 'Failed to update settings',
        variant: 'destructive',
      });
    }
  };

  const toggleAutoRun = async (enabled: boolean) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: user.id,
          auto_run_smart_notes: enabled,
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      setAutoRunSmartNotes(enabled);
      
      toast({
        title: enabled ? 'Auto-run Enabled' : 'Auto-run Disabled',
        description: enabled 
          ? 'Smart Notes will run automatically when AI-enhancing contacts'
          : 'You will need to manually run Smart Notes',
      });
    } catch (error) {
      console.error('[Smart Notes] Failed to toggle auto-run:', error);
      toast({
        title: 'Error',
        description: 'Failed to update settings',
        variant: 'destructive',
      });
    }
  };

  const addTask = async (title: string, prompt: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      if (tasks.length >= 5) {
        toast({
          title: 'Limit Reached',
          description: 'You can only have up to 5 research tasks',
          variant: 'destructive',
        });
        return false;
      }

      const { data, error } = await supabase
        .from('smart_notes_tasks')
        .insert({
          user_id: user.id,
          title,
          prompt,
          is_default: tasks.length === 0, // First task is default
        })
        .select()
        .single();

      if (error) throw error;

      setTasks(prev => [data, ...prev]);
      
      if (tasks.length === 0) {
        setDefaultTaskId(data.id);
      }

      toast({
        title: 'Task Added',
        description: `"${title}" has been added to your research tasks`,
      });

      return true;
    } catch (error) {
      console.error('[Smart Notes] Failed to add task:', error);
      toast({
        title: 'Error',
        description: 'Failed to add research task',
        variant: 'destructive',
      });
      return false;
    }
  };

  const updateTask = async (id: string, updates: Partial<Pick<SmartNotesTask, 'title' | 'prompt'>>) => {
    try {
      const { error } = await supabase
        .from('smart_notes_tasks')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      setTasks(prev => prev.map(t => 
        t.id === id ? { ...t, ...updates } : t
      ));

      toast({
        title: 'Task Updated',
        description: 'Research task has been updated',
      });

      return true;
    } catch (error) {
      console.error('[Smart Notes] Failed to update task:', error);
      toast({
        title: 'Error',
        description: 'Failed to update research task',
        variant: 'destructive',
      });
      return false;
    }
  };

  const deleteTask = async (id: string) => {
    try {
      const { error } = await supabase
        .from('smart_notes_tasks')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setTasks(prev => prev.filter(t => t.id !== id));
      
      if (defaultTaskId === id) {
        const newDefault = tasks.find(t => t.id !== id);
        if (newDefault) {
          await setDefaultTask(newDefault.id);
        } else {
          setDefaultTaskId(null);
        }
      }

      toast({
        title: 'Task Deleted',
        description: 'Research task has been removed',
      });

      return true;
    } catch (error) {
      console.error('[Smart Notes] Failed to delete task:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete research task',
        variant: 'destructive',
      });
      return false;
    }
  };

  const setDefaultTask = async (id: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Unset all defaults
      await supabase
        .from('smart_notes_tasks')
        .update({ is_default: false })
        .eq('user_id', user.id);

      // Set new default
      const { error } = await supabase
        .from('smart_notes_tasks')
        .update({ is_default: true })
        .eq('id', id);

      if (error) throw error;

      setTasks(prev => prev.map(t => ({
        ...t,
        is_default: t.id === id
      })));
      
      setDefaultTaskId(id);

      toast({
        title: 'Default Task Set',
        description: 'This task will be used by default',
      });

      return true;
    } catch (error) {
      console.error('[Smart Notes] Failed to set default task:', error);
      toast({
        title: 'Error',
        description: 'Failed to set default task',
        variant: 'destructive',
      });
      return false;
    }
  };

  return {
    isLoading,
    enableSmartNotes,
    autoRunSmartNotes,
    tasks,
    defaultTaskId,
    toggleSmartNotes,
    toggleAutoRun,
    addTask,
    updateTask,
    deleteTask,
    setDefaultTask,
    refresh: loadSettings,
  };
}